(function () {
    'use strict';

    useScheduledExportsFactory.$inject = ['hUser'];

    function useScheduledExportsFactory(hUser) {
        return function ($scope) {
            const DATE_ENDS_TYPES = {
                'END_DATE': 'END_DATE',
                'AFTER_OCCURRENCES': 'AFTER_OCCURRENCES',
                'NEVER': 'NEVER'
            };
            
            const REPORT_VIEW_TYPES = {
                SYSTEM: 'SYSTEM'
            };
            
            const state = {
                scheduledExports: [],
                searchedScheduledExports: [],
                exportsUsedThisMonthTotal: 0,
                page: -1,
                size: 10,
                sortType: '',
                sort: '',
                isLastEmpty: false,
                searchTerm: ''
            };

            async function getScheduleExports(sortType, isLoadMore) {
                if (state.isLastEmpty) {
                    return;
                }

                if (sortType) {
                    state.sortType = sortType;

                    if (!isLoadMore) {
                        state.sort = state.sort === 'ASC' ? 'DESC' : 'ASC';
                    }
                } else {
                    state.sortType = null;
                    state.sort = null;
                }

                const brandId = hUser.getActiveBrandIdWithAccess();

                state.page++;

                let response;
                try {
                    response = await GoodMorningToolEngineAPI.getScheduleExports(
                        brandId,
                        state.page,
                        state.size,
                        state.searchTerm,
                        state.sort,
                        state.sortType
                    );
                } catch (e) {
                    console.error(e);
                    return;
                }

                if (!response.configs?.length) {
                    state.isLastEmpty = true;
                }

                if (!!state.searchTerm) {
                    state.searchedScheduledExports = state.searchedScheduledExports.concat(response.configs);
                } else {
                    state.scheduledExports = state.scheduledExports.concat(response.configs);
                    state.exportsUsedThisMonthTotal = response.exports_used_this_month_total;
                }
            }

            function getLastRunStatusColor(status) {
                if (status === "FAILED") return 'danger';
                if (status === "IN_PROGRESS") return 'warning';

                return 'success';
            }

            function getLastRunTooltip(lastRunStatus) {
                switch (lastRunStatus.status) {
                    case 'SUCCESS':
                        return `Successful on ${lastRunStatus.channelType}`;
                    case 'IN_PROGRESS':
                        return `Running on ${lastRunStatus.channelType}`;
                    case 'FAILED':
                        return `Failed on ${lastRunStatus.channelType}, ${lastRunStatus.errorMessage}`;
                }

                return '';
            }

            function getScheduleFormats(schedule) {
                const formats = [];
                const lastRunStatuses = schedule.last_run_status || [];

                for (const lastRunStatus of lastRunStatuses) {
                    for (const format of lastRunStatus.formats) {
                        if (!formats.includes(format)) {
                            formats.push(format);
                        }
                    }
                }

                return formats;
            }

            async function deleteSchedule(id) {
                hConfirm({
                    title: '',
                    text: $filter("translate")("This action will delete the current schedule permanently"),
                    confirmButton: $filter("translate")("Delete"),
                    cancelButton: $filter("translate")("Cancel"),
                    callback: async (confirmed) => {
                        if (confirmed) {
                            const brandId = hUser.getActiveBrandIdWithAccess();
                            const updatedScheduledExports = state.scheduledExports.filter(item => item.id !== id);
                            const updatedSearchedScheduledExports = state.searchedScheduledExports.filter(item => item.id !== id);
                            const originalScheduledExports = JSON.parse(JSON.stringify(state.scheduledExports));
                            const originalSearchedScheduledExports = JSON.parse(JSON.stringify(state.searchedScheduledExports));

                            state.scheduledExports = updatedScheduledExports;
                            state.searchedScheduledExports = updatedSearchedScheduledExports;

                            try {
                                await GoodMorningToolEngineAPI.deleteScheduleExport(brandId, id);
                            } catch (e) {
                                console.error(e);
                                state.scheduledExports = originalScheduledExports;
                                state.searchedScheduledExports = originalSearchedScheduledExports;
                                $scope.$digest();
                                return;
                            }

                            await onScheduleExportOperationSuccess();
                            $scope.$digest();
                        }
                    }
                });
            }

            async function onScheduleExportOperationSuccess() {
                resetGetScheduleExportParams();
                await getScheduleExports();
                $scope.$digest();
            }

            function prepareSftpPath(path) {
                if (!path || path === '/') {
                    return './.';
                }
                path = path || '';

                const pathParts = path.split('/');
                if (!pathParts?.[0]) {
                    pathParts.splice(0, 1);
                }

                path = path?.[0] === '/' ?  path = '/'+pathParts.join('/') : pathParts.join('/')
                return path;
            }

            const canShowExternalEmailOption = (reportCode) => {
                return REPORTS_WITH_EXTERNAL_EMAIL.includes(reportCode);
            }

            function prepareCreateEditSchedulePayload(data) {
                const channels = [];

                if (data.sftpInputSwitch) {
                    channels.push({
                        channel_type: 'SFTP',
                        formats: data.sftpFileFormats.map(sftpFileFormat => sftpFileFormat.code),
                        meta_data: JSON.stringify({
                            hostname: data.sftpHostNameOrAddress,
                            port: data.sftpPortNumber,
                            path: prepareSftpPath(data.sftpPath, data.sftpUsername),
                            username: data.sftpUsername,
                            password: data.sftpPassword
                        })
                    });
                }

                if (data.emailInputSwitch) {
                    let emailAddress;
                    const supportsExternalEmail = canShowExternalEmailOption(data.report?.code);
                    
                    if (supportsExternalEmail && data.emailType === 'EXTERNAL') {
                        emailAddress = data.externalEmail;
                    } else {
                        emailAddress = data.recipientEmail?.email || data.usersLoginEmail;
                    }
                    
                    const emailChannel = {
                        channel_type: 'EMAIL',
                        formats: data.emailFileFormats.map(emailFileFormat => emailFileFormat.code),
                        meta_data: JSON.stringify({
                            email: emailAddress
                        })
                    };
                    
                    if (supportsExternalEmail) {
                        emailChannel.email_type = data.emailType || 'INTERNAL';
                    }
                    
                    channels.push(emailChannel);
                }

                let endTypeMetaData = null;
                if (data.ends === 'END_DATE') {
                    endTypeMetaData = data.endsOn ? moment(data.endsOn).format('YYYY-MM-DD') : null;
                }

                if (data.ends === 'AFTER_OCCURRENCES') {
                    endTypeMetaData = data.occurrences ? data.occurrences : null;
                }

                const payload = {
                    name: data.exportName,
                    description: data.exportDescription,
                    view_id: data.reportView.id,
                    report_view_id: data.report.id,
                    channels,
                    frequency: data.frequency.code,
                    start_date: moment(data.startDate).format('YYYY-MM-DD'),
                    start_time: `${data.startTimeHour.code}:${data.startTimeMinutes.code}`,
                    timezone: data.timezone.code,
                    end_type: hasNoEnd(data) ? DATE_ENDS_TYPES.NEVER : data.ends,
                    end_type_meta_data: endTypeMetaData
                };

                const isRiskDashboard = data.report?.code === 'RISK_ANALYSIS_DASHBOARD';
                const shouldSetRecipientUserId = data.reportView?.type === REPORT_VIEW_TYPES.SYSTEM && 
                    (!isRiskDashboard || data.emailType === EMAIL_TYPES.INTERNAL);
                
                if (shouldSetRecipientUserId) {
                    if (data.recipientEmail?.harri_id) {
                        payload.recipient_user_id = data.recipientEmail.harri_id;
                    }
                    else {
                        payload.recipient_user_id =  hUser.data.user.id;
                    }
                }

                return payload;
            }

            function hasNoEnd(data) {
                if (data.ends === DATE_ENDS_TYPES.END_DATE && !data.endsOn) return true;
                if (data.ends === DATE_ENDS_TYPES.AFTER_OCCURRENCES && !data.occurrences) return true;

                return data.frequency.code === 'ONCE';
            }

            function setSearchTerm(searchTerm) {
                state.searchTerm = searchTerm;
            }

            function resetGetScheduleExportParams() {
                if (state.searchTerm) {
                    state.searchedScheduledExports = [];
                } else {
                    state.scheduledExports = [];
                }
                state.page = -1;
                state.isLastEmpty = false;
            }

            return {
                state,
                getScheduleExports,
                getLastRunStatusColor,
                getLastRunTooltip,
                getScheduleFormats,
                deleteSchedule,
                onScheduleExportOperationSuccess,
                prepareCreateEditSchedulePayload,
                resetGetScheduleExportParams,
                setSearchTerm,
                prepareSftpPath
            };
        };
    }

    angular
        .module('harriApp')
        .factory('useScheduledExportsFactory', useScheduledExportsFactory);
})();